import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

// ==============================|| HOOKS - LOCAL STORAGE ||============================== //

export default function useLocalStorage<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue === null ? defaultValue : (JSON.parse(storedValue) as T);
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        // Preserved as-is: a cross-tab removal of this key (e.newValue === null) sets state to
        // null rather than falling back to defaultValue — pre-existing behavior, not introduced
        // here (see docs/PENDING-FINDINGS.md).
        setValue(e.newValue ? (JSON.parse(e.newValue) as T) : (e.newValue as T));
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage: Dispatch<SetStateAction<T>> = (newValue) => {
    setValue((currentValue) => {
      const result = typeof newValue === 'function' ? (newValue as (prev: T) => T)(currentValue) : newValue;
      localStorage.setItem(key, JSON.stringify(result));
      return result;
    });
  };

  return [value, setValueInLocalStorage];
}
